/**
 * Achilles Premium Badge Popup Handler
 * Handles click interactions for premium product badges with popup modal
 */

(function() {
  'use strict';

  let currentPopup = null;
  let currentOverlay = null;

  // Initialize all premium badges
  function initPremiumBadges() {
    const badges = document.querySelectorAll('[data-premium-badge]');

    badges.forEach(badge => {
      // Remove existing listeners to prevent duplicates
      badge.removeEventListener('click', handleBadgeClick);
      badge.addEventListener('click', handleBadgeClick);
    });

    // Setup close button listeners
    const closeButtons = document.querySelectorAll('[data-premium-close]');
    closeButtons.forEach(button => {
      button.removeEventListener('click', closePopup);
      button.addEventListener('click', closePopup);
    });

    // Setup overlay close listeners
    const overlays = document.querySelectorAll('[data-premium-overlay]');
    overlays.forEach(overlay => {
      overlay.removeEventListener('click', closePopup);
      overlay.addEventListener('click', closePopup);
    });

    // Close popup on Escape key
    document.removeEventListener('keydown', handleEscapeKey);
    document.addEventListener('keydown', handleEscapeKey);
  }

  // Handle badge click
  function handleBadgeClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const badge = e.currentTarget;
    const productId = badge.getAttribute('data-product-id');

    const gridItem = badge.closest('.grid-product__content');
    if (!gridItem) return;

    const popup = gridItem.querySelector(`[data-premium-popup][data-product-id="${productId}"]`);
    const overlay = gridItem.querySelector(`[data-premium-overlay][data-product-id="${productId}"]`);

    if (!popup || !overlay) return;

    // Close any currently open popup
    closeAllPopups();

    // Move overlay and popup to body for proper fixed positioning on mobile
    document.body.appendChild(overlay);
    document.body.appendChild(popup);

    // Open this popup
    openPopup(popup, overlay);
  }

  // Open popup
  function openPopup(popup, overlay) {
    currentPopup = popup;
    currentOverlay = overlay;

    // Add visible class with a slight delay for animation
    requestAnimationFrame(() => {
      overlay.classList.add('is-visible');
      popup.classList.add('is-visible');
    });

    // Prevent body scroll
    document.body.classList.add('achilles-premium-popup-open');

    // Trap focus within popup
    trapFocus(popup);
  }

  // Close popup
  function closePopup(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (currentPopup && currentOverlay) {
      currentPopup.classList.remove('is-visible');
      currentOverlay.classList.remove('is-visible');

      // Allow body scroll
      document.body.classList.remove('achilles-premium-popup-open');

      currentPopup = null;
      currentOverlay = null;
    }
  }

  // Close all open popups
  function closeAllPopups() {
    const popups = document.querySelectorAll('[data-premium-popup]');
    const overlays = document.querySelectorAll('[data-premium-overlay]');

    popups.forEach(popup => popup.classList.remove('is-visible'));
    overlays.forEach(overlay => overlay.classList.remove('is-visible'));

    document.body.classList.remove('achilles-premium-popup-open');

    currentPopup = null;
    currentOverlay = null;
  }

  // Handle Escape key press
  function handleEscapeKey(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      closePopup();
    }
  }

  // Trap focus within popup for accessibility
  function trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus the close button when popup opens
    const closeButton = element.querySelector('[data-premium-close]');
    if (closeButton) {
      closeButton.focus();
    }

    // Handle tab navigation
    element.addEventListener('keydown', function(e) {
      if (e.key === 'Tab' || e.keyCode === 9) {
        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPremiumBadges);
  } else {
    initPremiumBadges();
  }

  // Re-initialize when product grid updates (for AJAX loading)
  if (window.theme && typeof window.theme.reinitProductGrid === 'function') {
    const originalReinit = window.theme.reinitProductGrid;
    window.theme.reinitProductGrid = function() {
      originalReinit.apply(this, arguments);
      initPremiumBadges();
    };
  }

  // Support for dynamic content loading (mutation observer)
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        const hasNewBadges = Array.from(mutation.addedNodes).some(node => {
          return node.nodeType === 1 &&
                 (node.matches('[data-premium-badge]') ||
                  node.querySelector('[data-premium-badge]'));
        });

        if (hasNewBadges) {
          initPremiumBadges();
        }
      }
    });
  });

  // Start observing
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Export for manual initialization if needed
  window.AchillesPremiumBadge = {
    init: initPremiumBadges,
    open: openPopup,
    close: closePopup,
    closeAll: closeAllPopups
  };
})();
